import {motion} from 'framer-motion';
import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Box, Heading, HStack, Icon, Link, Show, Text, VStack} from 'platform/foundation';
import {useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {UseFormReturn} from 'react-hook-form';
import {Trans} from 'react-i18next';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {EmailResponseBody, useGetCustomerV2Query} from '@dms/api/customer';
import {
  BusinessCaseResponseBody,
  useLazyGetBusinessCaseQuery,
  useSendBusinessCaseBuyingOnlineOfferCustomerMessageMutation,
  useSendBusinessCaseCombinedOnlineOfferCustomerMessageMutation,
  useSendBusinessCaseOnlineOfferCustomerMessageMutation,
  useSendBusinessCaseOnlineOfferMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {getWorkspaceFromUri, urlBuilder, vehiclesRoutes} from '@dms/routes';

import {buildArray, composePath, Nullish} from 'shared/web';

import {localeByLanguage} from '../../constants/localeByLanguage';
import {useEmailBodyImageUpload} from '../../hooks/useEmailBodyImageUpload';
import {useTenant} from '../../hooks/useTenant';
import {createSearchParamsString} from '../../utils/createSearchParamsString';
import {getNaturalPersonFullName} from '../../utils/getNaturalPersonFullName';
import {handleApiError} from '../../utils/handleApiError';
import {toEmailBodyPayload} from '../../utils/toEmailBodyPayload';
import {useSmtpOptions} from '../SendCustomerEmailForm/hooks/useSmtpOptions';
import {EmailPreview} from './components/EmailPreview';
import {MessageFormField} from './components/MessageFormField';
import {FormValues, SendOnlineOfferEmailFormSchema} from './types/SendOnlineOfferEmailFormSchema';

type SendEmailOnlineOfferDialogBaseProps = {
  businessCaseData: Pick<BusinessCaseResponseBody, 'id' | 'customerId'>;
  sellerId: string;
  photoId: string | Nullish;
  onSuccess?: () => void;
};

/** Null only for a Business Case with offer alternatives but no deal vehicle yet. */
type SellingVehicleProps = {vehicleId: string | null};

type SellingOfferRequiredProps = SendEmailOnlineOfferDialogBaseProps &
  SellingVehicleProps & {
    onlineOfferKind: 'SELLING';
    shouldCreateOffer: false;
    onlineOfferUri: string;
    sellerEmail?: never;
  };

type SellingOfferNotRequiredProps = SendEmailOnlineOfferDialogBaseProps &
  SellingVehicleProps & {
    onlineOfferKind: 'SELLING';
    shouldCreateOffer: true;
    onlineOfferUri?: never;
    sellerEmail: string;
  };

type BuyingOfferProps = SendEmailOnlineOfferDialogBaseProps & {
  onlineOfferKind: 'BUYING';
  vehicleId: string;
  offerId: string;
};

type CombinedOfferProps = SendEmailOnlineOfferDialogBaseProps & {
  onlineOfferKind: 'COMBINED';
  vehicleId: string;
  buyingVehicleId: string;
  sellingOnlineOfferUri: string | Nullish;
  buyingOnlineOfferUri: string | Nullish;
};

type SendEmailOnlineOfferDialogProps =
  SellingOfferRequiredProps | SellingOfferNotRequiredProps | BuyingOfferProps | CombinedOfferProps;

const {workspace} = getWorkspaceFromUri();

/**
 * Constants for calculating email preview height.
 * The preview should fill the available dialog space with 24px margin from browser edges.
 * As long as it is not necessary anywhere else, we can keep this one time calculation here.
 * In the future, we will need to edit platform/dialog
 */
const DIALOG_VERTICAL_MARGIN_PX = 48; // 24px top + 24px bottom
const DIALOG_HEADER_HEIGHT_PX = 65; // padding 32px + heading 24px + 8px + border 1px
const DIALOG_BODY_PADDING_VERTICAL_PX = 32; // 16px top + 16px bottom
const VSTACK_SPACING_PX = 16; // spacing={4} between HStack and ButtonGroup
const BUTTON_GROUP_HEIGHT_PX = 32; // button default height

const TOTAL_FIXED_HEIGHT_PX =
  DIALOG_VERTICAL_MARGIN_PX +
  DIALOG_HEADER_HEIGHT_PX +
  DIALOG_BODY_PADDING_VERTICAL_PX +
  VSTACK_SPACING_PX +
  BUTTON_GROUP_HEIGHT_PX;

const EMAIL_PREVIEW_HEIGHT = `calc(100vh - ${TOTAL_FIXED_HEIGHT_PX}px)`;

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function SendOnlineOfferEmailForm(props: SendEmailOnlineOfferDialogProps) {
  const {language} = useLocale();
  const {isMultiLocale, tenant} = useTenant();
  const [sendEmailOnlineOffer, {isLoading: isSendEmailOnlineOfferMutationLoading}] =
    useSendBusinessCaseOnlineOfferCustomerMessageMutation();
  const [sendBuyingOnlineOffer, {isLoading: isSendBuyingOnlineOfferMutationLoading}] =
    useSendBusinessCaseBuyingOnlineOfferCustomerMessageMutation();
  const [sendCombinedOnlineOffer, {isLoading: isSendCombinedOnlineOfferMutationLoading}] =
    useSendBusinessCaseCombinedOnlineOfferCustomerMessageMutation();
  const [getBusinessCase, {isLoading: isGetBusinessCaseQueryLoading}] =
    useLazyGetBusinessCaseQuery();
  const [createOnlineOffer, {isLoading: isCreateOnlineOfferMutationLoading}] =
    useSendBusinessCaseOnlineOfferMutation();
  const {data: customerData, isLoading: isInitialDataLoading} = useGetCustomerV2Query({
    customerId: props.businessCaseData.customerId,
  });
  const [customEmail, setCustomEmail] = useState<string | null>(null);

  const defaultOfferLocale = customerData?.preferredLocale ?? tenant?.defaultLocale ?? undefined;
  const effectiveOfferLocale = isMultiLocale && defaultOfferLocale ? defaultOfferLocale : language;
  const emailBodyUpload = useEmailBodyImageUpload();

  const emailPreviewLocale = localeByLanguage[effectiveOfferLocale] ?? effectiveOfferLocale;

  const {
    smtpCredentialsOptions,
    defaultSmtpCredentialId,
    isLoading: isLoadingSmtpOptions,
  } = useSmtpOptions(
    match(props.onlineOfferKind)
      .with('BUYING', () => 'business_case-buying_online_offer' as const)
      .with('COMBINED', () => 'business_case-combined_online_offer' as const)
      .otherwise(() => 'business_case-online_offer' as const)
  );

  const isLoading =
    isSendEmailOnlineOfferMutationLoading ||
    isSendBuyingOnlineOfferMutationLoading ||
    isSendCombinedOnlineOfferMutationLoading ||
    isGetBusinessCaseQueryLoading ||
    isCreateOnlineOfferMutationLoading ||
    isLoadingSmtpOptions;

  const isEmailFormLoading = isLoadingSmtpOptions || isInitialDataLoading;

  const contactPersonsOptionsUnfiltered = buildArray<{
    label: string;
    value: string;
    emails: EmailResponseBody[];
  }>()
    .when(isNotNil(customerData?.foundingPerson), {
      label: getNaturalPersonFullName(customerData?.foundingPerson) ?? '',
      value: customerData?.foundingPerson?.id ?? '',
      emails: customerData?.foundingPerson?.emails ?? [],
    })
    .when(
      isNotNilOrEmpty(customerData?.contacts),
      ...(customerData?.contacts || []).map((contact) => ({
        label: getNaturalPersonFullName(contact) ?? '',
        value: contact.id ?? '',
        emails: contact.emails ?? [],
      }))
    );

  const contactPersonsOptions = Array.from(
    new Map(
      [...contactPersonsOptionsUnfiltered].reverse().map((item) => [item.value, item])
    ).values()
  ).reverse();

  const firstContactPersonValue = contactPersonsOptions[0]?.value ?? undefined;
  const firstEmailValue = contactPersonsOptions[0]?.emails[0]?.email ?? undefined;

  const finish = () => {
    props.onSuccess?.();
    closeCurrentDialog();
  };

  const onSubmit: FormSubmitHandler<FormValues> = (values, _setErrors, _reset, blockerContext) => {
    // One guard for every offer kind: they share the editor.
    if (!emailBodyUpload.ensureBodyReadyToSend(values.body, blockerContext)) {
      return Promise.resolve();
    }

    return match(props)
      .with({onlineOfferKind: 'BUYING'}, (buyingProps) =>
        sendBuyingOnlineOffer({
          sendBuyingOnlineOfferCustomerMessageRequestBody: {
            businessCaseId: buyingProps.businessCaseData.id,
            offerId: buyingProps.offerId,
            vehicleId: buyingProps.vehicleId,
            photoId: buyingProps.photoId ?? null,
            sellerId: buyingProps.sellerId,
            ...toEmailBodyPayload(values.body),
            customerEmailAddress: values.emailAddress,
            customerId: buyingProps.businessCaseData.customerId,
            emailCredentialsId: values.smtpCredentialsId,
          },
        })
          .unwrap()
          .then(finish)
          .catch(handleApiError)
      )
      .with({onlineOfferKind: 'COMBINED'}, (combinedProps) =>
        sendCombinedOnlineOffer({
          sendCombinedOnlineOfferCustomerMessageRequestBody: {
            businessCaseId: combinedProps.businessCaseData.id,
            sellerId: combinedProps.sellerId,
            ...toEmailBodyPayload(values.body),
            customerEmailAddress: values.emailAddress,
            customerId: combinedProps.businessCaseData.customerId,
            emailCredentialsId: values.smtpCredentialsId,
          },
        })
          .unwrap()
          .then(finish)
          .catch(handleApiError)
      )
      .with({shouldCreateOffer: true}, (sellingProps) =>
        createOnlineOffer({businessCaseId: sellingProps.businessCaseData.id})
          .unwrap()
          .then(() => getBusinessCase({businessCaseId: sellingProps.businessCaseData.id}).unwrap())
          .then((newBcData) => {
            if (!newBcData?.offerId) {
              closeCurrentDialog();
            }
            const onlineOfferUri = urlBuilder({
              workspace,
              protocol: window.location.protocol,
              domain: process.env.ONLINE_OFFER_DOMAIN!,
              route: `/${newBcData.offerId}`,
              searchParam: createSearchParamsString({
                lang: effectiveOfferLocale,
                sellerEmail: sellingProps.sellerEmail,
                openedBy: 'email',
              }),
            });
            return sendEmailOnlineOffer({
              createOnlineOfferCustomerMessageRequestBody: {
                businessCaseId: sellingProps.businessCaseData.id,
                vehicleId: sellingProps.vehicleId,
                photoId: sellingProps.photoId ?? null,
                sellerId: sellingProps.sellerId,
                customerEmailAddress: values.emailAddress,
                ...toEmailBodyPayload(values.body),
                customerId: sellingProps.businessCaseData.customerId,
                smtpCredentialsId: values.smtpCredentialsId,
                onlineOfferUri,
              },
            })
              .unwrap()
              .then(finish)
              .catch(handleApiError);
          })
          .catch(handleApiError)
      )
      .otherwise((sellingProps) =>
        sendEmailOnlineOffer({
          createOnlineOfferCustomerMessageRequestBody: {
            businessCaseId: sellingProps.businessCaseData.id,
            vehicleId: sellingProps.vehicleId,
            photoId: sellingProps.photoId ?? null,
            sellerId: sellingProps.sellerId,
            customerEmailAddress: values.emailAddress,
            ...toEmailBodyPayload(values.body),
            customerId: sellingProps.businessCaseData.customerId,
            smtpCredentialsId: values.smtpCredentialsId,
            onlineOfferUri: sellingProps.onlineOfferUri,
          },
        })
          .unwrap()
          .then(finish)
          .catch(handleApiError)
      );
  };

  const handleSelectContactPerson = (
    selectedContactPersonId: string | number | null,
    formApi: UseFormReturn<FormValues>
  ) => {
    if (typeof selectedContactPersonId !== 'string') {
      // @ts-ignore is needed because default value can be undefined but not in validation schema.
      formApi.setValue('emailAddress', undefined);
      return;
    }
    const selectedContact = contactPersonsOptions.find(
      (contact) => contact.value === selectedContactPersonId
    );
    if (!selectedContact || !selectedContact.emails[0]?.email) {
      // @ts-ignore is needed because default value can be undefined but not in validation schema.
      formApi.setValue('emailAddress', undefined);
      return;
    }
    formApi.setValue('emailAddress', selectedContact.emails[0]?.email, {shouldValidate: true});
  };

  return (
    <DataStatus isLoading={isEmailFormLoading}>
      <Box position="relative">
        <motion.div>
          <Form
            defaultValues={{
              contactPerson: firstContactPersonValue,
              emailAddress: firstEmailValue,
              smtpCredentialsId: defaultSmtpCredentialId,
              body: '',
            }}
            experimentalZodSchema={SendOnlineOfferEmailFormSchema}
            onSubmit={onSubmit}
          >
            {(control, formApi) => {
              const contactPersonValue = formApi.getValues('contactPerson');
              const selectedContact = contactPersonsOptions.find(
                (contact) => contact.value === contactPersonValue
              );

              const emailAddressesFromSelectedContact = [
                ...(customEmail ? [{label: customEmail, value: customEmail}] : []),
                ...(selectedContact?.emails?.map((email) => ({
                  label: email.email,
                  value: email.email,
                })) ?? []),
              ];
              const emailPreviewProps = match(props)
                .with({onlineOfferKind: 'BUYING'}, (buyingProps) => ({
                  customerMessageId: 'business_case-buying_online_offer' as const,
                  vehicleId: buyingProps.vehicleId,
                  offerId: buyingProps.offerId,
                }))
                .with({onlineOfferKind: 'COMBINED'}, (combinedProps) => ({
                  customerMessageId: 'business_case-combined_online_offer' as const,
                  vehicleId: combinedProps.vehicleId,
                  buyingVehicleId: combinedProps.buyingVehicleId,
                  sellingOnlineOfferUri: combinedProps.sellingOnlineOfferUri,
                  buyingOnlineOfferUri: combinedProps.buyingOnlineOfferUri,
                }))
                .otherwise((sellingProps) => ({
                  customerMessageId: 'business_case-online_offer' as const,
                  vehicleId: sellingProps.vehicleId,
                  onlineOfferUri: sellingProps.onlineOfferUri,
                }));

              return (
                <VStack spacing={4}>
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <VStack spacing={4}>
                        <Box paddingHorizontal={10}>
                          <VStack spacing={4} align="center" justify="center">
                            <Icon
                              value="action/mail_upload"
                              color="palettes.blue.30.100"
                              isNotFilled
                              size={20}
                            />

                            <Heading size={3}>
                              {i18n.t('entity.sendEmailOnlineOfferDialog.innerHeading')}
                            </Heading>
                            <Show when={props.vehicleId}>
                              {(vehicleId) => (
                                <Text color="secondary" align="center">
                                  <Trans
                                    i18nKey="entity.sendEmailOnlineOfferDialog.description"
                                    components={{
                                      vehiclelink: (
                                        <Link
                                          href={composePath(vehiclesRoutes.photos, {
                                            params: {id: vehicleId, tab: 'vehicle-photos'},
                                          })}
                                          target="_blank"
                                        />
                                      ),
                                    }}
                                  />
                                </Text>
                              )}
                            </Show>
                          </VStack>
                        </Box>

                        <FormField
                          control={control}
                          name="smtpCredentialsId"
                          type="choice"
                          label={i18n.t('entity.customer.labels.sender')}
                          options={smtpCredentialsOptions}
                          isNotClearable
                          isRequired
                        />

                        <Box width="100%">
                          <HStack width="100%" spacing={4}>
                            <Box flex={1}>
                              <FormField
                                control={control}
                                name="contactPerson"
                                type="choice"
                                label={i18n.t(
                                  'entity.sendEmailOnlineOfferDialog.contactPersonLabel'
                                )}
                                options={contactPersonsOptions}
                                onChange={(value) => handleSelectContactPerson(value, formApi)}
                                isRequired
                              />
                            </Box>
                            <Box flex={1}>
                              <FormField
                                control={control}
                                name="emailAddress"
                                type="creatableChoice"
                                options={emailAddressesFromSelectedContact}
                                label={i18n.t(
                                  'entity.sendEmailOnlineOfferDialog.emailAddressLabel'
                                )}
                                isDisabled={isNil(selectedContact)}
                                onCreateOption={(newValue) => {
                                  setCustomEmail(newValue);
                                  formApi.setValue('emailAddress', newValue, {
                                    shouldValidate: true,
                                  });
                                }}
                                formatCreateLabel={(value) =>
                                  `${i18n.t('general.actions.create')} ${value}`
                                }
                                isRequired
                              />
                            </Box>
                          </HStack>
                        </Box>

                        <VStack spacing={4}>
                          <MessageFormField
                            control={control}
                            formApi={formApi}
                            recordId={props.businessCaseData.id}
                            preferredLocale={customerData?.preferredLocale}
                            upload={emailBodyUpload}
                          />
                        </VStack>
                      </VStack>
                    </Box>

                    <Box flex={1} height={EMAIL_PREVIEW_HEIGHT} overflow="auto">
                      <EmailPreview
                        {...emailPreviewProps}
                        businessCaseId={props.businessCaseData.id}
                        photoId={props.photoId}
                        sellerId={props.sellerId}
                        locale={emailPreviewLocale}
                        formApi={formApi}
                      />
                    </Box>
                  </HStack>

                  <ButtonGroup align="right">
                    <Button
                      onClick={closeCurrentDialog}
                      variant="secondary"
                      title={i18n.t('general.actions.cancel')}
                      isDisabled={isLoading}
                    />

                    <FormButton
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.send')}
                      isLoading={isLoading}
                    />
                  </ButtonGroup>
                </VStack>
              );
            }}
          </Form>
        </motion.div>
      </Box>
    </DataStatus>
  );
}
